import React, { useEffect, useMemo } from 'react';
import { Button, Container, Paper, Stack, Typography } from '@mui/material';
import NewApplicantModal from './NewApplicantModal';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { destroyApplicantAsync, fetchApplicantsAsync, selectApplicants } from './applicantsSlice';
import {
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { fetchAttemptsAsync, selectAttempts } from '../../applicant/TestView/attemptSlice';
import { setDialog, setNotification } from '../../../app/appSlice';
import { useNavigate } from 'react-router-dom';
import { setPageName } from '../../../app/appSlice';
import { Applicant } from '../../../generated/api';
import { ContentCopy, Delete } from '@mui/icons-material';
import { DialogState } from '../../../app/types';

const ApplicantsView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const applicantState = useAppSelector(selectApplicants);
  const attemptSlice = useAppSelector(selectAttempts);

  useEffect(() => {
    dispatch(setPageName('Applicants'));
    dispatch(fetchApplicantsAsync());
    dispatch(fetchAttemptsAsync());
  }, []);

  const getAttemptStatus = (params: GridValueGetterParams) => {
    const attempt = attemptSlice.allAttempts.find((a) => a.applicant === params.id);
    if (!attempt) return '-';
    return attempt.endTime.toLocaleString('en-FI');
  };

  const deleteApplicant = (applicant: Applicant) => () => {
    const dialog: DialogState = {
      open: true,
      title: `Are you sure you want to delete Applicant "${applicant.firstName} ${applicant.lastName}"?`,
      description: 'This action cannot be undone.',
      onOk: () => dispatch(destroyApplicantAsync(applicant.id))
    };
    dispatch(setDialog(dialog));
  };

  const renderCode = ({ value, hasFocus }: GridRenderCellParams<string>) => {
    const handleClick = async () => {
      if (!value) return;
      await navigator.clipboard.writeText(value);
      dispatch(setNotification({ message: 'Code copied to clipboard!', type: 'success' }));
    };

    return (
      <>
        <Typography>{value}</Typography>
        {hasFocus && (
          <Button onClick={handleClick}>
            <ContentCopy />
          </Button>
        )}
      </>
    );
  };

  const columns = useMemo<GridColumns<Applicant>>(
    () => [
      { field: 'firstName', headerName: 'First Name', flex: 1 },
      { field: 'lastName', headerName: 'Last name', flex: 1 },
      {
        field: 'code',
        headerName: 'Code',
        type: 'string',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        renderCell: renderCode
      },
      {
        field: 'attemptCompleted',
        headerName: 'Attempt Completed',
        valueGetter: getAttemptStatus,
        flex: 1,
        headerAlign: 'center',
        align: 'center'
      },
      {
        field: 'actions',
        type: 'actions',
        maxWidth: 50,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem key={1} label="delete" icon={<Delete />} onClick={deleteApplicant(params.row)} />
        ]
      }
    ],
    [deleteApplicant]
  );

  const handleRowDoubleClicked = (params: GridRowParams) => {
    const attempt = attemptSlice.allAttempts.find((a) => a.applicant === params.id);
    if (!attempt) {
      dispatch(setNotification({ message: 'Applicant has not started attempt yet', type: 'error' }));
      return;
    }
    navigate(`${attempt.id}`);
  };

  return (
    <Container>
      <Paper>
        <Stack spacing={1} padding={1}>
          <NewApplicantModal />
          <DataGrid
            autoHeight
            disableSelectionOnClick
            columns={columns}
            rows={applicantState.applicants}
            onRowDoubleClick={handleRowDoubleClicked}
            sx={{
              boxShadow: 0,
              border: 0,
              '&.MuiDataGrid-root .MuiDataGrid-cell': { py: 1 }
            }}
          />
        </Stack>
      </Paper>
    </Container>
  );
};

export default ApplicantsView;
